import type {
  BusinessGetResponseType,
  UserCreateResponseType,
} from "@sellar-io/api-v2/types";
import { matchPath } from "react-router";
import * as ROUTES from "../constants/routes";
import { getJwtUrl } from "../pages/auth/signup/signupUrl";
import {
  generateLoginLink,
  getContinueUrlFromOnboardingUrlStorage,
} from "../utils/sellarHelpers";
import { resolveAuthenticatedAccessMode } from "./resolveAuthenticatedAccessMode";

export type AppRedirectState = {
  hasFirebaseUser: boolean;
  sellarUser: UserCreateResponseType | null;
  business: BusinessGetResponseType | null;
  continueUrl: string | null;
  queryParameters: string | null;
  onboardingContinueUrl: string | null;
  redirectToMarket: boolean;
  redirectToSellarCreditOnboarding: boolean;
  ignoreContinuationRedirects?: boolean;
};

type ContinueRouteDefinition = {
  patterns: string[];
  resolve: (continueUrl: string) => string;
};

const CONTINUE_ROUTE_DEFINITIONS: ContinueRouteDefinition[] = [
  {
    patterns: [ROUTES.SUPPLIER.pattern],
    resolve: (continueUrl) => `${continueUrl}/order`,
  },
];

const resolveContinuePath = (continueUrl: string): string => {
  const definition = CONTINUE_ROUTE_DEFINITIONS.find(({ patterns }) =>
    patterns.some((pattern) => matchPath(pattern, continueUrl)),
  );

  return definition?.resolve(continueUrl) ?? continueUrl;
};

const appendQueryParameters = (
  pathname: string,
  queryParameters: string,
): string => {
  const normalizedQueryParameters = queryParameters.replace(/^\?+/, "");

  return normalizedQueryParameters
    ? `${pathname}?${normalizedQueryParameters}`
    : pathname;
};

const resolveContinueUrl = ({
  continueUrl,
  queryParameters,
  onboardingContinueUrl,
}: Pick<
  AppRedirectState,
  "continueUrl" | "queryParameters" | "onboardingContinueUrl"
>): string => {
  const jwtSearchParam = getJwtUrl(continueUrl);
  if (jwtSearchParam) return `${ROUTES.JWT}?jwt=${jwtSearchParam}`;

  if (onboardingContinueUrl) {
    return getContinueUrlFromOnboardingUrlStorage(onboardingContinueUrl);
  }

  if (!continueUrl) return "";
  if (!queryParameters) return continueUrl;

  return appendQueryParameters(
    resolveContinuePath(continueUrl),
    queryParameters,
  );
};

export const resolveAppRedirect = ({
  hasFirebaseUser,
  sellarUser,
  business,
  continueUrl,
  queryParameters,
  onboardingContinueUrl,
  redirectToMarket,
  redirectToSellarCreditOnboarding,
  ignoreContinuationRedirects = false,
}: AppRedirectState): string => {
  if (!ignoreContinuationRedirects && continueUrl) {
    if (business || (sellarUser && continueUrl.startsWith("jwt"))) {
      return resolveContinueUrl({
        continueUrl,
        queryParameters,
        onboardingContinueUrl,
      });
    }

    const queryParametersParam = queryParameters
      ? `&queryParameters=${queryParameters}`
      : "";
    return `${ROUTES.SIGNUP}?continue=${continueUrl}${queryParametersParam}`;
  }

  if (sellarUser && business) {
    if (
      resolveAuthenticatedAccessMode({ business, sellarUser }) !== "operational"
    ) {
      return ROUTES.SUPPLIER_ONBOARDING;
    }

    if (
      !ignoreContinuationRedirects &&
      onboardingContinueUrl &&
      ![ROUTES.LANDING, ROUTES.SIGNUP].includes(onboardingContinueUrl)
    ) {
      return getContinueUrlFromOnboardingUrlStorage(onboardingContinueUrl);
    }

    if (business.type === "supplier") return ROUTES.ORDERS;
    if (redirectToSellarCreditOnboarding) {
      return `${ROUTES.DISCOVER}?showSellarCreditOnboarding=true`;
    }
    if (redirectToMarket) return ROUTES.DISCOVER;
    return ROUTES.SUPPLIERS;
  }

  if (hasFirebaseUser) return ROUTES.SIGNUP;

  return generateLoginLink();
};
